import { all, fork, call, put, select, takeLatest } from 'redux-saga/effects';
import api from '@api/api';
import * as actions from '@store/actions/appViewAction';
import { toast } from 'react-toastify';
import { getApiErrorMessage } from '@common/utils/apiHelper';
import { SHOW_MODAL } from '@store/actions/commonAction';

function* searchAppViewList() {
  const { searchParams } = yield select(state => state.appViewReducer);

  const response = yield call(api.searchSubscriptionList, {
    params: {
      searchStartDate: searchParams.searchStartDate,
      searchEndDate: searchParams.searchEndDate,
      searchState: searchParams.searchState,
      pageIndex: 1,
      mode: 'SM', // gradeTypeCode 미사용, 청약내용확인 메뉴의 mode값은 'SM'으로 고정
      eappReadStatCd: searchParams.eappReadStatCd,
    },
    payload: {
      searchName: searchParams.searchText,
      pageConditionKey: '',
      pageCount: 10,
      pageNextYn: '',
    },
  });

  const { status, data } = response;

  if (status === 200) {
    const { list, pageConditionKey, pageCount, pageNextYn, pageTotalCount } =
      data.pagingResult;
    yield put({
      type: actions.SAVE_APP_VIEW_STATE,
      payload: {
        appViewList: list.map(row => {
          return {
            ...row,
            applicationLabel:
              row.omniYn === 'Y'
                ? '옴니'
                : row.applicationYn === 'Y'
                ? '전자'
                : '서면',
          };
        }),
        searchParams: {
          pageConditionKey,
          pageCount,
          pageNextYn,
          pageTotalCount,
        },
      },
    });
  } else {
    const errorMessage = getApiErrorMessage(
      response,
      '청약 내용 확인 조회에 실패하였습니다.',
    );

    yield put({
      type: SHOW_MODAL,
      payload: {
        visible: true,
        type: 'info',
        msg: errorMessage,
      },
    });
  }
}

function* infiniteSearchAppViewList() {
  const { searchParams, appViewList } = yield select(
    state => state.appViewReducer,
  );

  const nextData = searchParams.pageNextYn.split(',');
  const isKb = nextData[0];
  const isPru = nextData[1];
  if (isKb === 'Y' || isPru === 'Y') {
    const response = yield call(api.searchSubscriptionList, {
      params: {
        searchStartDate: searchParams.searchStartDate,
        searchEndDate: searchParams.searchEndDate,
        searchState: searchParams.searchState,
        pageIndex: searchParams.pageIndex,
        mode: 'SM', // gradeTypeCode 미사용, 청약내용확인 메뉴의 mode값은 'SM'으로 고정
        eappReadStatCd: searchParams.eappReadStatCd,
      },
      payload: {
        searchName: searchParams.searchText,
        pageConditionKey: searchParams.pageConditionKey,
        pageCount: 10,
        pageNextYn: searchParams.pageNextYn,
      },
    });
    const { status, data } = response;

    if (status === 200) {
      const { list, pageConditionKey, pageCount, pageNextYn, pageTotalCount } =
        data.pagingResult;
      yield put({
        type: actions.SAVE_APP_VIEW_STATE,
        payload: {
          appViewList: appViewList.concat(
            list.map(row => {
              return {
                ...row,
                applicationLabel:
                  row.omniYn === 'Y'
                    ? '옴니'
                    : row.applicationYn === 'Y'
                    ? '전자'
                    : '서면',
              };
            }),
          ),
          searchParams: {
            pageConditionKey,
            pageCount,
            pageNextYn,
            pageTotalCount,
          },
        },
      });
    } else {
      const errorMessage = getApiErrorMessage(
        response,
        '청약 내용 확인 조회에 실패하였습니다.',
      );

      yield put({
        type: SHOW_MODAL,
        payload: {
          visible: true,
          type: 'info',
          msg: errorMessage,
        },
      });
    }
  }
}

function* saveAppViewState({ payload }) {
  const { appViewList } = yield select(state => state.appViewReducer);
  if (payload.policyPdfUrl === '') {
    yield put({
      type: actions.SAVE_APP_VIEW_SEARCH_PARAMS,
      payload: { size: appViewList.length },
    });
  }
}

function* readEappPolicyPdfUrl({ payload }) {
  const response = yield call(api.getPolicyPdfUrl, {
    params: {
      applicationNo: payload.applicationNo,
      agentNo: payload.agentNo,
    },
  });
  const { status, data } = response;

  if (status === 200) {
    const policyPdfUrl = $utils.makeEaPdfUrl(data.message);

    yield put({
      type: actions.SAVE_APP_VIEW_STATE,
      payload: { policyPdfUrl },
    });
  } else {
    const errorMessage = getApiErrorMessage(
      response,
      '청약서 url 조회에 실패하였습니다.',
    );

    yield put({
      type: SHOW_MODAL,
      payload: {
        visible: true,
        type: 'info',
        msg: errorMessage,
      },
    });
  }
}

function* readEappComplete({ payload }) {
  const { appViewList } = yield select(state => state.appViewReducer);
  const response = yield call(api.readEappComplete, {
    params: { applicationNo: payload.applicationNo },
  });

  const { status } = response;

  if (status === 200) {
    yield put({
      type: actions.SAVE_APP_VIEW_STATE,
      payload: { policyPdfUrl: '' },
    });

    toast('청약내용 확인이 완료 되었습니다.');

    yield put({
      type: actions.SAVE_APP_VIEW_SEARCH_PARAMS,
      payload: { size: appViewList.length },
    });
  } else {
    const errorMessage = getApiErrorMessage(
      response,
      '청약 내용 확인에 실패하였습니다. 잠시후 다시 확인해주시기 바랍니다.',
    );
    // status - 400 : 권한이 없는 사용자 입니다. (그외 정의되지 않은 에러는 400과 동일하게 detail 표시 또는 임의 에러 메시지 표시)
    toast(errorMessage);
  }
}

// watcher
function* watchAppView() {
  yield takeLatest(actions.SEARCH_APP_VIEW_LIST, searchAppViewList);
  yield takeLatest(
    actions.INFINITE_SEARCH_APP_VIEW_LIST,
    infiniteSearchAppViewList,
  );
  yield takeLatest(actions.SAVE_APP_VIEW_SEARCH_PARAMS, searchAppViewList);
  yield takeLatest(actions.SAVE_APP_VIEW_STATE, saveAppViewState);

  yield takeLatest(actions.READ_EAPP_POLICY_PDF_URL, readEappPolicyPdfUrl);
  yield takeLatest(actions.READ_EAPP_COMPLETE, readEappComplete);
}

export default function* appViewSaga() {
  yield all([fork(watchAppView)]);
}
